import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, Pencil, AlertCircle, GraduationCap, Pause, Play } from "lucide-react";
import { http } from "../../lib/http";
import { useAuth } from "../../contexts/AuthContext";
import { PageHeader, PageBody } from "../../components/shell/PageChrome";
import ExportMenu from "../../components/ExportMenu";
import StatusBadge from "../../components/StatusBadge";
import ConfirmDialog from "../../components/data/ConfirmDialog";
import { DataTable, PageToolbar, PrimaryAddButton } from "../../components/data/DataTable";
import TablePagination from "../../components/data/TablePagination";
import ViewDrawer from "../../components/data/ViewDrawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import EnrollmentWizard from "../enrollment/EnrollmentWizard";

const CEFR = ["pre_a1", "a1", "a1_plus", "a2", "a2_plus", "b1", "b1_plus", "b2", "b2_plus", "c1", "c1_plus", "c2"];

export default function StudentsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = ["admin", "receptionist"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [families, setFamilies] = useState([]);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("all");
  const [active, setActive] = useState("all");
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [hasAlert, setHasAlert] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (level !== "all") p.set("level", level);
      if (active !== "all") p.set("active", active === "active");
      if (hasAlert) p.set("has_alert", true);
      p.set("skip", (page - 1) * PAGE_SIZE);
      p.set("limit", PAGE_SIZE);
      const r = await http.get(`/students?${p}`);
      setItems(r.data.items);
      setTotal(r.data.total ?? (r.data.items || []).length);
    } finally { setLoading(false); }
  }, [q, level, active, hasAlert, page]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { setPage(1); }, [q, level, active, hasAlert]);
  useEffect(() => {
    if (canEdit) http.get(`/families?limit=200&active=true`).then((r) => setFamilies(r.data.items)).catch(() => {});
  }, [canEdit]);

  const openView = async (row) => {
    const r = await http.get(`/students/${row.id}`);
    setView(r.data);
  };

  const save = async (data, id) => {
    try {
      if (id) await http.patch(`/students/${id}`, data);
      else await http.post(`/students`, data);
      toast.success(t("students.saved"));
      setEdit(null);
      fetchList();
    } catch (e) {
      const errs = e.response?.data?.detail?.errors || [e.response?.data?.detail || "Save failed"];
      toast.error(Array.isArray(errs) ? errs.join("; ") : String(errs));
    }
  };

  const columns = [
    {
      key: "name", label: t("students.name"),
      render: (r) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-[#0A1F44]">{r.first_name} {r.last_name}</span>
          {r.has_alerts && <span data-testid={`student-alert-dot-${r.id}`} className="inline-flex items-center gap-1 text-[#C8102E]"><AlertCircle size={14} /></span>}
        </div>
      ),
    },
    { key: "age", label: t("students.age"), render: (r) => r.age ?? "—" },
    { key: "level", label: t("students.level"), render: (r) => <Badge variant="outline" className="uppercase">{(r.level || "").replace("_plus", "+").toUpperCase()}</Badge> },
    { key: "family", label: t("students.family"), render: (r) => r.family_name || "—" },
    { key: "phone", label: t("students.phone"), render: (r) => r.phone || "—" },
    { key: "active", label: t("students.status"), render: (r) => <StatusBadge status={r.active !== false ? "active" : "inactive"} /> },
    {
      key: "actions", label: t("common.actions"), thClass: "text-right", tdClass: "text-right",
      render: (r) => {
        const canActivate = user?.role === "admin";
        const canDeactivate = user?.role === "admin" || user?.role === "receptionist";
        const showToggle = (r.active !== false && canDeactivate) || (r.active === false && canActivate);
        return (
          <div className="flex items-center justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={() => openView(r)} data-testid={`student-view-${r.id}`} aria-label="View"><Eye size={14} /></Button>
            {canEdit && <Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`student-edit-${r.id}`} aria-label="Edit"><Pencil size={14} /></Button>}
            {showToggle && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setStatusConfirm(r)}
                data-testid={`student-toggle-active-${r.id}`}
                aria-label={r.active !== false ? "Deactivate" : "Activate"}
                title={r.active !== false ? "Deactivate" : "Activate"}
              >
                {r.active !== false ? <Pause size={14} className="text-slate-600" /> : <Play size={14} className="text-emerald-700" />}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div data-testid="students-page">
      <PageHeader title={t("students.title")} subtitle={t("students.subtitle")} />
      <PageBody>
        <div className="flex flex-wrap items-center justify-end gap-2 mb-4" data-testid="students-actions-row">
          {canEdit && (
            <>
              <Button onClick={() => setEnrolling(true)} variant="outline" data-testid="students-enroll-button">
                <GraduationCap size={16} className="mr-1.5" />{t("students.enrollNew")}
              </Button>
              <PrimaryAddButton label={t("students.addStudent")} onClick={() => setEdit(emptyStudent(families[0]?.id || ""))} testid="students-add-button" />
            </>
          )}
          <ExportMenu resource="students" />
        </div>
        <PageToolbar
          testid="students-toolbar"
          search={q} onSearch={setQ}
          filters={(
            <>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger className="w-[120px] bg-white" data-testid="students-filter-level"><SelectValue placeholder="Level" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  {CEFR.map((l) => <SelectItem key={l} value={l}>{l.replace("_plus", "+").toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={active} onValueChange={setActive}>
                <SelectTrigger className="w-[120px] bg-white" data-testid="students-filter-active"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  <SelectItem value="active">{t("common.active")}</SelectItem>
                  <SelectItem value="inactive">{t("common.inactive")}</SelectItem>
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={hasAlert} onCheckedChange={(v) => setHasAlert(!!v)} data-testid="students-filter-alerts" />
                {t("students.hasAlerts")}
              </label>
            </>
          )}
          rightExtra={<TablePagination page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} testid="students-pagination-top" />}
        />
        {loading ? <div className="text-slate-500">{t("common.loading")}</div> : (
          <>
            <DataTable
              testid="students-table"
              columns={columns}
              rows={items}
              onRowDoubleClick={(r) => canEdit ? setEdit(r) : openView(r)}
              emptyText={t("students.empty")}
            />
            <div className="mt-4 flex justify-end">
              <TablePagination page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} testid="students-pagination-bottom" />
            </div>
          </>
        )}
      </PageBody>

      <ViewDrawer open={!!view} onClose={() => setView(null)} title={view ? `${view.first_name} ${view.last_name}` : ""} testid="student-drawer">
        {view && <StudentDetail student={view} onAlertsChanged={() => { fetchList(); }} onClose={() => setView(null)} />}
      </ViewDrawer>

      <ConfirmDialog
        open={!!statusConfirm}
        title={statusConfirm?.active !== false ? t("students.deactivateTitle", "Deactivate student?") : t("students.activateTitle", "Activate student?")}
        message={statusConfirm?.active !== false
          ? t("students.deactivateBody", `"${statusConfirm?.first_name || ""} ${statusConfirm?.last_name || ""}" will be hidden from class enrolment pickers and no further charges will accrue.`)
          : t("students.activateBody", `Re-activating will allow assigning "${statusConfirm?.first_name || ""}" to classes again.`)
        }
        confirmText={statusConfirm?.active !== false ? t("students.deactivate", "Deactivate") : t("students.activate", "Activate")}
        danger={statusConfirm?.active !== false}
        onCancel={() => setStatusConfirm(null)}
        onConfirm={async () => {
          const r = statusConfirm; setStatusConfirm(null);
          if (!r) return;
          try {
            const action = r.active !== false ? "deactivate" : "activate";
            await http.post(`/students/${r.id}/${action}`);
            toast.success(action === "deactivate"
              ? t("students.deactivated", "Student deactivated")
              : t("students.activated", "Student activated"));
            fetchList();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
          }
        }}
        testid="confirm-toggle-active"
      />

      <StudentEditModal
        open={!!edit}
        initial={edit}
        families={families}
        onCancel={() => setEdit(null)}
        onSave={(d) => save(d, edit?.id)}
      />

      <EnrollmentWizard
        open={enrolling}
        onClose={() => setEnrolling(false)}
        onCommitted={() => { setEnrolling(false); fetchList(); }}
      />
    </div>
  );
}

function emptyStudent(family_id) {
  return {
    family_id, first_name: "", last_name: "", preferred_name: "", dni_nie: "",
    date_of_birth: "2015-01-01", level: "a1", phone: "", email: "",
    active: true, emergency_contact: { name: "", relationship: "", phone: "" },
    allergies: [],
  };
}

function StudentDetail({ student, onAlertsChanged }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = ["admin", "receptionist", "teacher"].includes(user?.role);
  const [adding, setAdding] = useState(false);
  const [alert, setAlert] = useState({ kind: "", severity: "medium", description: "" });
  const [noteText, setNoteText] = useState("");

  const addAlert = async () => {
    if (!alert.kind.trim()) return;
    await http.post(`/students/${student.id}/medical-alerts`, alert);
    toast.success("Alert added");
    setAlert({ kind: "", severity: "medium", description: "" });
    setAdding(false);
    onAlertsChanged?.();
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    await http.post(`/students/${student.id}/notes`, { text: noteText });
    toast.success("Note added");
    setNoteText("");
    onAlertsChanged?.();
  };

  return (
    <div className="space-y-6 text-sm" data-testid="student-detail">
      {(student.medical_alerts || []).length > 0 && (
        <div className="bg-red-50 border-2 border-[#C8102E] rounded-xl p-4" data-testid="student-medical-banner">
          <div className="flex items-center gap-2 text-[#C8102E] font-semibold mb-2">
            <AlertCircle size={18} /> {t("students.medicalAlerts")}
          </div>
          <ul className="space-y-1.5">
            {student.medical_alerts.map((a) => (
              <li key={a.id} className="text-sm">
                <span className="font-medium">{a.kind}</span>
                <Badge className={`ml-2 ${a.severity === "high" ? "bg-[#C8102E] text-white" : a.severity === "medium" ? "bg-amber-200 text-amber-900" : "bg-slate-200"}`}>{a.severity}</Badge>
                {a.description && <div className="text-slate-600 mt-0.5">{a.description}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">{t("students.section.personal")}</h3>
        <div className="grid grid-cols-2 gap-2">
          <span className="text-slate-500">DOB</span><span className="text-[#0A1F44]">{student.date_of_birth} ({student.age}y)</span>
          <span className="text-slate-500">Level</span><span className="text-[#0A1F44]">{(student.level || "").toUpperCase()}</span>
          <span className="text-slate-500">Phone</span><span className="text-[#0A1F44]">{student.phone || "—"}</span>
          <span className="text-slate-500">Email</span><span className="text-[#0A1F44]">{student.email || "—"}</span>
        </div>
      </div>

      {canEdit && (
        <div>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">{t("students.section.addAlert")}</h3>
          {adding ? (
            <div className="space-y-2">
              <Input placeholder="e.g. Severe peanut allergy" value={alert.kind} onChange={(e) => setAlert({ ...alert, kind: e.target.value })} data-testid="alert-kind-input" />
              <Select value={alert.severity} onValueChange={(v) => setAlert({ ...alert, severity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Description / instructions" value={alert.description} onChange={(e) => setAlert({ ...alert, description: e.target.value })} />
              <div className="flex gap-2">
                <Button size="sm" onClick={addAlert} data-testid="alert-save-btn" className="bg-[#0A1F44] text-white">Save</Button>
                <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="alert-add-trigger">{t("students.addAlert")}</Button>
          )}
        </div>
      )}

      <div>
        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">{t("students.section.notes")}</h3>
        {(student.notes_internal || []).length === 0 ? <p className="text-slate-500">—</p> : (
          <ul className="space-y-1.5">
            {student.notes_internal.map((n) => (
              <li key={n.id} className="border-b border-slate-100 pb-1.5">
                <div className="text-[#0A1F44]">{n.text}</div>
                <div className="text-xs text-slate-400">{n.created_at}</div>
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <div className="mt-2 flex gap-2">
            <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder={t("students.addNotePlaceholder")} data-testid="student-note-input" />
            <Button size="sm" onClick={addNote} data-testid="student-note-save" className="bg-[#0A1F44] text-white">Add</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function StudentEditModal({ open, initial, families, onCancel, onSave }) {
  const { t } = useTranslation();
  const [d, setD] = useState(initial || {});
  useEffect(() => { setD(initial || {}); }, [initial]);
  if (!open) return null;
  const u = (k, v) => setD({ ...d, [k]: v });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="student-edit-modal">
        <DialogHeader><DialogTitle>{initial?.id ? t("students.edit") : t("students.createTitle")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label={t("students.first")} required><Input value={d.first_name || ""} onChange={(e) => u("first_name", e.target.value)} data-testid="student-first-input" /></Field>
          <Field label={t("students.last")} required><Input value={d.last_name || ""} onChange={(e) => u("last_name", e.target.value)} data-testid="student-last-input" /></Field>
          <Field label={t("students.dob")} required><Input type="date" value={d.date_of_birth || ""} onChange={(e) => u("date_of_birth", e.target.value)} /></Field>
          <Field label={t("students.level")}>
            <Select value={d.level || "a1"} onValueChange={(v) => u("level", v)}>
              <SelectTrigger data-testid="student-level-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CEFR.map((l) => <SelectItem key={l} value={l}>{l.replace("_plus", "+").toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("students.family")} required>
            <Select value={d.family_id || ""} onValueChange={(v) => u("family_id", v)}>
              <SelectTrigger data-testid="student-family-select"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {families.map((f) => <SelectItem key={f.id} value={f.id}>{f.family_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="DNI/NIE"><Input value={d.dni_nie || ""} onChange={(e) => u("dni_nie", e.target.value.toUpperCase())} /></Field>
          <Field label={t("students.phone")}><Input value={d.phone || ""} onChange={(e) => u("phone", e.target.value)} /></Field>
          <Field label={t("students.email")}><Input type="email" value={d.email || ""} onChange={(e) => u("email", e.target.value)} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="student-cancel-btn">{t("common.cancel")}</Button>
          <Button onClick={() => onSave(d)} className="bg-[#0A1F44] text-white" data-testid="student-save-btn">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}{required ? <span className="text-[#C8102E]"> *</span> : null}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
